import * as R from "ramda";

import { splitInlineJSONAtIndex } from "./inline_json";

////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////

export function childBlockNodes(data: BlockContent) {
  switch(data.type) {
    case "activity":
    case "multipleChoice":
    case "numberedList":
    case "regularList":
    case "section":
      return data.content;
    case "imageGallery":
      return data.images;
    case "matcher":
      return [...data.contentA, ...data.contentB];
    default:
      return [];
  }
}

export function childBlockKeysAndNodes(data: BlockContent): Array<[string, Array<BlockContent>]> {
  switch(data.type) {
    case "activity":
    case "multipleChoice":
    case "numberedList":
    case "regularList":
    case "section":
      return [
        ["content", data.content] as const,
      ];
    case "imageGallery":
      return [
        ["images", data.images] as const,
      ];
    case "matcher":
      return [
        ["contentA", data.contentA] as const,
        ["contentB", data.contentB] as const,
      ];
    default:
      return [];
  }
}

export function deepFindNode(data: PossibleContent, tester: (node: BlockContent) => boolean): BlockContent | undefined {
  if (data instanceof Array) {
    return data.reduce((acc: BlockContent | undefined, childNode: PossibleContent) => acc || deepFindNode(childNode, tester), undefined) as BlockContent | undefined;
  } else {
    if (tester(data)) { return data; }

    return deepFindNode(childBlockNodes(data), tester);
  }
}

export function deepFindNodeLineage(data: PossibleContent, tester: (node: BlockContent) => boolean): Array<BlockContent> | undefined {
  if (data instanceof Array) {
    return data.reduce((acc: Array<BlockContent> | undefined, childNode: PossibleContent) => acc || deepFindNodeLineage(childNode, tester), undefined) as Array<BlockContent> | undefined;
  } else {
    if (tester(data)) { return [data]; }

    const result = deepFindNodeLineage(childBlockNodes(data), tester);

    if (result) {
      return [data].concat(result);
    } else {
      return undefined;
    }
  }
}

export function deepFindNodePath(data: PossibleContent, tester: (node: BlockContent) => boolean): Array<string | number> | undefined {
  if (data instanceof Array) {
    return data.reduce((acc: Array<string | number> | undefined, childNode: PossibleContent, index: number) => {
      if (acc) {
        return acc;
      } else {
        const returnVal =  deepFindNodePath(childNode, tester);

        if (returnVal) {
          return ([index] as Array<string | number>).concat(returnVal);
        } else {
          return undefined;
        }
      }
    }, undefined);
  } else {
    if (tester(data)) { return []; }

    const blockKeysAndNodes = childBlockKeysAndNodes(data);

    return blockKeysAndNodes.reduce((acc: Array<string | number> | undefined, childNode: [string, Array<BlockContent>]) => {
      if (acc) {
        return acc;
      } else {
        const returnVal = deepFindNodePath(childNode[1], tester);
        if (returnVal) {
          return ([childNode[0]] as Array<string | number>).concat(returnVal);
        } else {
          return undefined;
        }
      }
    }, undefined);
  }
}

export function deepFindAllNodes(data: PossibleContent, tester: (node: BlockContent) => boolean): Array<BlockContent> {
  if (data instanceof Array) {
    return data.flatMap((childNode: PossibleContent) => deepFindAllNodes(childNode, tester));
  } else {
    const childResults = deepFindAllNodes(childBlockNodes(data), tester);
    if (tester(data)) {
      return [data].concat(childResults);
    } else {
      return childResults;
    }
  }
}

////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////

export function deepFindByUID(data: PossibleContent, uid: string) {
  return(deepFindNode(data, (datum) => (datum.type !== undefined) && (datum.uid === uid)));
}

export function deepFindLineageByUID(data: PossibleContent, uid: string) {
  return(deepFindNodeLineage(data, (datum) => (datum.type !== undefined) && (datum.uid === uid)));
}

export function deepFindAncestorsByUID(data: PossibleContent, uid: string) {
  const lineage = deepFindLineageByUID(data, uid);

  return lineage && R.init(lineage);
}

export function deepFindParentByUID(data: PossibleContent, uid: string) {
  const lineage = deepFindLineageByUID(data, uid);

  return lineage && R.last(R.init(lineage));
}

export function deepFindSiblingBeforeByUID(data: PossibleContent, uid: string): BlockContent | undefined {
  const path = deepFindPathByUID(data, uid);

  if (!path) { return undefined; }

  const lastIndex = R.last(path);

  if (typeof lastIndex === "number" && lastIndex > 0) {
    const siblingLineage = R.update(-1, lastIndex - 1, path);

    return R.view(R.lensPath(siblingLineage), data);
  } else {
    return undefined;
  }
}

export function deepFindOldestNotSectionAncestorByUID(data: PossibleContent, uid: string) {
  const lineage = deepFindLineageByUID(data, uid);

  if (!lineage) { return undefined; }

  return R.find((node) => node.type !== "section", lineage);
}

export function deepFindYoungestSectionAncestorByUID(data: PossibleContent, uid: string) {
  const lineage = deepFindLineageByUID(data, uid);

  if (!lineage) { return undefined; }

  return R.findLast((node) => node.type === "section", lineage);
}

export function deepFindPathByUID(data: PossibleContent, uid: string) {
  return(deepFindNodePath(data, (datum) => (datum.type !== undefined) && (datum.uid === uid)));
}

////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////

export function insertBeforeNodeByUID<T extends PossibleContent>(data: T, uid: string, toAdd: BlockContent) {
  const path = deepFindPathByUID(data, uid);

  if (!path) { return data; }

  const newIndex = R.last(path);

  if (typeof newIndex !== "number") { return data; }

  return R.over(R.lensPath(R.init(path)), R.insert(newIndex, toAdd), data);
}

export function insertAfterNodeByUID<T extends PossibleContent>(data: T, uid: string, toAdd: BlockContent) {
  const path = deepFindPathByUID(data, uid);

  if (!path) { return data; }

  const oldIndex = R.last(path);

  if (typeof oldIndex !== "number") { return data; }

  const newIndex = oldIndex + 1;

  return R.over(R.lensPath(R.init(path)), R.insert(newIndex, toAdd), data);
}

export function insertAfterNodeByUIDAtSectionLevel<T extends PossibleContent>(data: T, uid: string, toAdd: BlockContent) {
  const lineage = deepFindLineageByUID(data, uid);

  if (!lineage) { return data; }

  const parentSection = R.findLast((node) => node.type === "section", lineage);

  if (!parentSection) { return data; }

  const pathFromParent = deepFindPathByUID(parentSection, uid);

  if (!pathFromParent) { return data; }

  const newParentContentKey = pathFromParent[0] as string;
  const newParentIndex = pathFromParent[1] as number + 1;

  const pathToParent = deepFindPathByUID(data, parentSection.uid);

  if (!pathToParent) { return data; }

  return R.over(R.lensPath(pathToParent.concat([newParentContentKey])), R.insert(newParentIndex, toAdd), data);
}

export function deleteNodeByUID<T extends PossibleContent>(data: T, uid: string) {
  const path = deepFindPathByUID(data, uid);

  if (!path) { return data; }

  const deleteIndex = R.last(path);

  if (typeof deleteIndex !== "number") { return data; }

  return R.over(R.lensPath(R.init(path)), R.remove(deleteIndex, 1), data);
}

export function replaceNodeByUID<T extends PossibleContent>(data: T, uid: string, toReplaceWith: BlockContent) {
  const path = deepFindPathByUID(data, uid);

  if (!path) { return data; }

  const replaceIndex = R.last(path);

  if (typeof replaceIndex !== "number") { return data; }

  return R.over(R.lensPath(R.init(path)), R.update(replaceIndex, toReplaceWith), data);
}

export function replaceNodeWithArrayByUID<T extends PossibleContent>(data: T, uid: string, toReplaceWith: Array<BlockContent>) {
  const path = deepFindPathByUID(data, uid);

  if (!path) { return data; }

  const indexToReplace = R.last(path);

  if (typeof indexToReplace !== "number") { return data; }

  const functionToReplaceWith = R.compose(R.insertAll(indexToReplace, toReplaceWith), R.remove(indexToReplace, 1) as any);

  return R.over(R.lensPath(R.init(path)), functionToReplaceWith, data);
}

export function setNodeValueByUIDAndKey<T extends PossibleContent>(data: T, uid: string, key: string, newValue: any) {
  const path = deepFindPathByUID(data, uid);

  if (!path) { return data; }

  const mediaTypeLens = R.lensPath(path.concat([key]));

  return R.set(mediaTypeLens, newValue, data);
}

export function modifyNodeValueByUIDAndKey<T extends PossibleContent, U>(data: T, uid: string, key: string, func: (node: U) => U) {
  const path = deepFindPathByUID(data, uid);

  if (!path) { return data; }

  const mediaTypeLens = R.lensPath(path.concat([key]));

  return R.over(mediaTypeLens, func, data);
}

export function siblingIndexByUID(data: PossibleContent, uid: string) {
  const path = deepFindPathByUID(data, uid);

  if (!path) { return undefined; }

  return R.last(path);
}

export function siblingCountByUID(data: PossibleContent, uid: string) {
  const path = deepFindPathByUID(data, uid);

  if (!path) { return undefined; }

  return R.length(R.view(R.lensPath(R.init(path)), data));
}

////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////

export function splitNodeAtIndex(node: BlockContent, contentKey: string, index: number) {
  const [content1, content2] = splitInlineJSONAtIndex((node as any)[contentKey] as InlineJsonContent, index);

  return [
    {
      ...node,
      [contentKey]: content1,
    },
    {
      ...node,
      [contentKey]: content2,
    },
  ];
}

////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////
import { v4 as uuidv4 } from "uuid";

export function generateNode<T extends BlockContentType>(type: T, args: Partial<BlockContentFromType<T>>={}): BlockContentFromType<T> {
  const base = {
    uid: uuidv4(),
    ...args,
  };

  switch (type) {
    case "paragraph":
    case "listItem":
      return {
        type: type,
        content: "",
        ...base,
      } as BlockContentFromType<T>;
    case "regularList":
    case "numberedList":
      return {
        type: type,
        content: [generateNode("listItem") as ListItemNode],
        ...base,
      } as BlockContentFromType<T>;
    case "audio":
    case "image":
      return {
        type: type,
        title : "",
        source: "local",
        uri: "",
        ...base,
      } as BlockContentFromType<T>;
    case "video":
      return {
        type: type,
        title : "",
        source: "reeldx",
        uri: "",
        ...base,
      } as BlockContentFromType<T>;
    default:
      return {
        type: type,
        ...base,
      } as BlockContentFromType<T>;
  }
}

////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////

export function newAction<T extends DocActionType>(action: T, args: Partial<DocActionFromType<T>["arguments"]>={}): DocActionFromType<T> {
  return {
    type: "action",
    action,
    arguments: args,
  } as DocActionFromType<T>;
}

export function newSetNodeKVPairAction(uid: string, key: string, value: unknown, targetType?: TargetType, targetId?: number) {
  return newAction("setNodeKVPair", { uid, key, value, targetType, targetId });
}

export function newOpenLinkAction(href: string) {
  return newAction("openLink", { href });
}

////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////

// TODO: FILTER ACTIONS BASED ON IF COLLAPSED BY IF OPEN ONCE ACTIVITY STATE APPLIED
export function addCloseDrawersButtonToContent(rootNode: SectionNode) {
  const sections = rootNode.content.filter((node) => node.type === "section")
  const activities = rootNode.content.filter((node) => node.type === "activity")
  const videoIndex = rootNode.content.findIndex((node) => node.type === "video")

  if ((sections.length === 0 && activities.length === 0) || videoIndex === -1) { return rootNode }

  const sectionActions = sections.map((node) => (
    {
      type: "action",
      action: "setNodeKVPair",
      arguments: {
        uid: node.uid,
        key: "collapsed",
        value: true,
      }
    } as DocAction
  ))

  const activityActions = activities.flatMap((activity) => (
    activity.content.flat().map((node) => (
      {
        type: "action",
        action: "setNodeKVPair",
        arguments: {
          targetType: "activity",
          targetId: activity.id,
          uid: node.uid,
          key: "collapsed",
          value: true,
        }
      } as DocAction
    ))
  ))

  const newButton = {
    type: "button",
    uid: uuidv4(),
    title: "Close Drawers",
    onClick: sectionActions.concat(activityActions),
  } as ButtonNode

  return {
    ...rootNode,
    content: R.insert(videoIndex + 1, newButton, rootNode.content)
  }
}
